import React, { FC, useMemo, useState } from "react";
import {
  Input,
  Form,
  Checkbox,
  Button,
  InputNumber,
  message,
  Select,
} from "antd";
import {
  ruleAddress,
  ruleLettersOnly,
  ruleNameMax,
  ruleNameMin,
  rulePatronymicMax,
  rulePatronymicMin,
  ruleSurnameMax,
  ruleSurnameMin,
} from "../../../../../../../constants/rules/RulesConstantsUser";
import { IMemberFL } from "../../../../../../../store/slice/applicationDataToBankSlice/applicationDataToBankSlice";
import {
  ruleRequired,
  ruleText,
} from "../../../../../../../constants/rules/RulesConstantsForms";
import { useParams } from "react-router-dom";
import { useForm } from "antd/es/form/Form";
import {
  ruleInnIndividual,
  ruleSnils,
} from "../../../../../../../constants/rules/RulesConstantsApplication";
import {
  validatePassportAddressIndex,
  validatePassportDivisionCode,
  validatePassportNumber,
  validatePassportSeries,
} from "../../../../../../../utils/utilities/validateDataToBank";

import dayjs from "dayjs";
import { UI_ITONumericInput } from "../../../../../../../UI_ITO/UI_ITONumericInput/UI_ITONumericInput";
import { Guard } from "../../../../../../../utils/guards/Guard";
import {
  APPLICATION_BANK_STATUS_KKB,
  BANK_NAME,
} from "../../../../../../../utils/guards/ApplicationBankStatusGuard/types";
import {
  useAddMemberFlMutation,
  useChangeMemberFlMutation,
} from "../../../../../../../store/api/applicationBankApi/individual.api";
import { useGetApplicationDataToBankQuery } from "../../../../../../../store/api/applicationBankApi/applicationBank.api";
import UI_ITOCodeDivisionInput from "../../../../../../../UI_ITO/UI_ITOCodeDivisionInput/UI_ITOCodeDivisionInput";

import { UI_ITOSelectAddressInput } from "../../../../../../../UI_ITO/UI_ITOSelectAddressInput/UI_ITOSelectAddressInput";
import { AutoCompleteProps } from "antd/lib";
import { UI_ITODatePicker } from "../../../../../../../UI_ITO/UI_ITODatePicker/UI_ITODatePicker";
import { getFIOPartsFromFIOString } from "../../../../../../../utils/utilities/getFIOPartsFromFIOString";
import { IIndividualCardInfoProps } from "../../../../../../../interfaces/props/IIndividualCardInfo";
import { ApiResponseType } from "../../../../../../../types/apiResponse";

import classes from "./style/IndividualCardInfo.module.css";
import { maxLengthInn, maxLengthPassportDivisionCode, maxLengthPassportNumber, maxLengthPassportPostIndex, maxLengthPassportSeries, maxLengthSnils } from "../../../../../../../constants/maxLengthInFolders";

export const IndividualCardInfo: FC<IIndividualCardInfoProps> = (props) => {
  const { applicationBankId, applicationId } = useParams();
  const [form] = useForm();

  const initialValues = useMemo(() => {
    const fioPartsFromNameString = getFIOPartsFromFIOString(props.name);

    return {
      inn: props.inn,
      is_eio: props.is_eio,
      acts_pursuant: props.acts_pursuant,
      is_founder: props.is_founder,
      position: props.position ?? "",
      snils: props.snils,
      percent: props.percent,
      post_and_registration_address: props.post_and_registration_address,
      ...props.member_passport_fl,
      passport_post_address: props.member_passport_fl?.passport_post_address,
      passport_issue_date: props.member_passport_fl?.passport_issue_date
        ? dayjs(props.member_passport_fl?.passport_issue_date)
        : undefined,
      passport_birth: props.member_passport_fl?.passport_birth
        ? dayjs(props.member_passport_fl?.passport_birth)
        : undefined,
      passport_surname:
        props.member_passport_fl?.passport_surname ||
        fioPartsFromNameString.surname,
      passport_name:
        props.member_passport_fl?.passport_name || fioPartsFromNameString.name,
      passport_patronymic:
        props.member_passport_fl?.passport_patronymic ||
        fioPartsFromNameString.patronymic,
    };
  }, [props]);
  const { openOrCloseForm } = props;
  const [addMemberFl] = useAddMemberFlMutation();
  const [changeMemberFl] = useChangeMemberFlMutation();
  const [isEioChecked, setIsEioChecked] = useState<boolean>(
    props.is_eio ?? false
  );
  const [isAddressChecked, setIsAddressChecked] = useState<boolean>(
    props.post_and_registration_address ?? false
  );
  const { data: applicationBankData } = useGetApplicationDataToBankQuery({
    applicationId: Number(applicationId),
    applicationBankId: Number(applicationBankId),
  });

  const addNewIndividual = () => {
    const formFields = form.getFieldsValue();

    form
      .validateFields()
      .then(() => {
        if (applicationBankId && formFields) {
          const newIndividual = {
            ...formFields,
            passport_series: formFields.passport_series?.replace(/[^0-9]/g, ""),
            passport_number: formFields.passport_number?.replace(/[^0-9]/g, ""),
            passport_division_code: formFields.passport_division_code?.replace(
              /[^0-9]/g,
              ""
            ),
            passport_post_index: +formFields.passport_post_index,
            is_eio: formFields.is_eio ?? false,
            is_founder: formFields.is_founder ?? false,
            post_and_registration_address:
              formFields.post_and_registration_address ?? false,
            passport_issue_date: dayjs(formFields.passport_issue_date).format(),
            passport_birth: dayjs(formFields.passport_birth).format(),
          };

          addMemberFl({
            applicationBankId: Number(applicationBankId),
            applicationId: Number(applicationId),
            body: newIndividual,
          })
            .then((res: ApiResponseType<IMemberFL>) => {
              if (!res.error) {
                openOrCloseForm();
                form.resetFields();
              }
            })
            .catch(() => {
              message.error("Данные некорректны");
            });
        }
      })
      .catch(() => {
        message.error("Данные некорректны");
      });
  };

  const onHandleSelectRegInput: AutoCompleteProps["onSelect"] = (_, option) => {
    if (option?.info?.zip) {
      form.setFieldValue("passport_post_index", String(option?.info?.zip));
    }
  };

  const changeIndividual = () => {
    const formFields = form.getFieldsValue();

    form
      .validateFields()
      .then(() => {
        if (applicationBankId && props.id) {
          const newIndividual = {
            ...formFields,
            passport_series: formFields.passport_series?.replace(/[^0-9]/g, ""),
            passport_number: formFields.passport_number?.replace(/[^0-9]/g, ""),
            passport_division_code: formFields.passport_division_code?.replace(
              /[^0-9]/g,
              ""
            ),
            passport_birth: formFields.passport_birth.format(),
            passport_issue_date: formFields.passport_issue_date.format(),
            passport_post_index: formFields.passport_post_index,
            is_eio: formFields.is_eio ?? false,
            is_founder: formFields.is_founder ?? false,
            post_and_registration_address:
              formFields.post_and_registration_address ?? false,
          };

          changeMemberFl({
            applicationBankId: Number(applicationBankId),
            applicationId: Number(applicationId),
            fl_id: props.id,
            body: newIndividual,
          }).then((res: ApiResponseType<IMemberFL>) => {
            if (res.error) {
              message.open({
                type: "error",
                content: res.error?.data?.message,
              });
            } else {
              openOrCloseForm();
            }
          });
        }
      })
      .catch(() => {
        message.error("Данные некорректны");
      });
  };

  return (
    <div className={classes.individualInfoContainer}>
      <Form
        layout="vertical"
        name={`individual_information_form_${props.id}`}
        initialValues={initialValues}
        form={form}
        autoComplete="off"
        scrollToFirstError={{ behavior: "smooth", block: "center" }}
      >
        <div className={classes.formInputWrapper}>
          <Form.Item
            label="Фамилия"
            rules={[
              ruleRequired,
              ruleLettersOnly,
              ruleSurnameMin,
              ruleSurnameMax,
            ]}
            name="passport_surname"
          >
            <Input placeholder="Введите фамилию" size="large" />
          </Form.Item>
          <Form.Item
            name="passport_name"
            label="Имя"
            rules={[ruleRequired, ruleLettersOnly, ruleNameMin, ruleNameMax]}
          >
            <Input placeholder="Введите имя" size="large" />
          </Form.Item>
          <Form.Item
            name="passport_patronymic"
            label="Отчество"
            rules={[ruleLettersOnly, rulePatronymicMin, rulePatronymicMax]}
          >
            <Input placeholder="Введите отчество" size="large" />
          </Form.Item>
          <Form.Item
            label="Дата рождения"
            name="passport_birth"
            rules={[ruleRequired]}
          >
            <UI_ITODatePicker
              placeholder="Введите дату рождения"
              style={{ width: "100%" }}
              size="large"
            />
          </Form.Item>
        </div>
        <div className={classes.formInputWrapper}>
          <Form.Item
            label="Серия"
            name="passport_series"
            rules={[ruleRequired, { validator: validatePassportSeries }]}
          >
            <UI_ITONumericInput placeholder="Введите cерию" maxLength={maxLengthPassportSeries} size="large" />
          </Form.Item>
          <Form.Item
            name="passport_number"
            label="Номер"
            rules={[ruleRequired, { validator: validatePassportNumber }]}
          >
            <UI_ITONumericInput placeholder="Введите номер" maxLength={maxLengthPassportNumber} size="large" />
          </Form.Item>
          <Form.Item
            name="passport_division_code"
            label="Код подразделения"
            rules={[ruleRequired, { validator: validatePassportDivisionCode }]}
          >
            <UI_ITOCodeDivisionInput
              placeholder="Введите код подразделения"
              type="tel"
              maxLength={maxLengthPassportDivisionCode}
              size="large"
            />
          </Form.Item>
          <Form.Item
            name="passport_issue_date"
            label="Дата выдачи"
            rules={[ruleRequired]}
          >
            <UI_ITODatePicker
              size="large"
              style={{ width: "100%" }}
              placeholder="Введите дату выдачи"
            />
          </Form.Item>
        </div>
        <Form.Item
          label="Кем выдан"
          name="passport_department"
          rules={[ruleAddress, ruleRequired]}
        >
          <Input placeholder="Введите кем выдан" size="large" />
        </Form.Item>
        <Form.Item
          label="Место рождения"
          name="passport_place_birth"
          rules={[ruleAddress, ruleRequired]}
        >
          <Input placeholder="Введите место рождения" size="large" />
        </Form.Item>
        <div className={classes.formInputWrapperAdress}>
          <Form.Item
            label="Индекс"
            style={{ width: "220px" }}
            name="passport_post_index"
            rules={[ruleRequired, { validator: validatePassportAddressIndex }]}
          >
            <UI_ITONumericInput size="large" placeholder="Введите индекс" maxLength={maxLengthPassportPostIndex} />
          </Form.Item>
          <Form.Item
            label="Адрес регистрации"
            style={{ flex: "1 0" }}
            name="passport_address"
            rules={[ruleAddress, ruleRequired]}
          >
            <UI_ITOSelectAddressInput size="large" onSelect={onHandleSelectRegInput} />
          </Form.Item>
        </div>

        <Form.Item valuePropName="checked" name="post_and_registration_address">
          <Checkbox onChange={() => setIsAddressChecked((prev) => !prev)} className={classes.individualCardInfoCheckbox}>
            Адрес регистрации совпадает с местом жительства
          </Checkbox>
        </Form.Item>
        {!isAddressChecked && (
          <Form.Item
            label={"Место жительства"}
            name={"passport_post_address"}
            rules={[ruleAddress, ruleRequired]}
          >
            <Input placeholder="Введите место жительства" size="large" />
          </Form.Item>
        )}
        <div className={classes.formInputWrapper}>
          <Form.Item
            label="ИНН"
            name="inn"
            rules={[ruleRequired, ruleInnIndividual]}
          >
            <UI_ITONumericInput size="large" placeholder="Введите ИНН" maxLength={maxLengthInn} />
          </Form.Item>
          <Form.Item
            label="Снилс"
            name="snils"
            rules={[ruleRequired, ruleSnils]}
          >
            <UI_ITONumericInput size="large" placeholder="Введите снилс" maxLength={maxLengthSnils} />
          </Form.Item>
        </div>
        <div className={classes.formInputWrapper}>
          <Form.Item
            valuePropName="checked"
            style={{ alignSelf: "end" }}
            name="is_founder"
          >
            <Checkbox className={classes.individualCardInfoCheckbox}>Является учредителем</Checkbox>
          </Form.Item>
          <Form.Item
            valuePropName="checked"
            style={{ alignSelf: "end" }}
            name="is_eio"
          >
            <Checkbox className={classes.individualCardInfoCheckbox} onChange={() => setIsEioChecked((prev) => !prev)}>
              Является ЕИО
            </Checkbox>
          </Form.Item>
        </div>
        <div className={classes.formInputWrapperSelect}>
          <Form.Item
            label="Доля УК физического лица"
            name="percent"
            rules={[ruleRequired]}
          >
            <InputNumber<number>
              style={{ width: "100%" }}
              placeholder="Введите долю УК"
              size="large"
              min={0}
              max={100}
              suffix="%"
              controls={false}
            />
          </Form.Item>
          {isEioChecked && (
            <>
              <Form.Item
                label="Действует на основании"
                name="acts_pursuant"
                rules={[ruleRequired]}
              >
                <Select
                  options={[
                    { value: "charter", label: "Устава" },
                    {
                      value: "powerOfAttorney",
                      label: "Доверенности",
                    },
                    { value: "decision", label: "Решения" },
                    { value: "protocol", label: "Протокола" },
                    { value: "order", label: "Приказа" },
                  ]}
                  size="large"
                />
              </Form.Item>
              <Form.Item
                label="Должность"
                rules={[ruleRequired, ruleText]}
                name="position"
              >
                <Input size="large" />
              </Form.Item>
            </>
          )}
        </div>

        {props?.inn ? (
          <div className={classes.buttonWrapper}>
            <Form.Item>
              {applicationBankData && (
                <>
                  <Guard.ApplicationBankStatus
                    status={applicationBankData?.status}
                    allowedStatuses={[
                      {
                        bank_name: BANK_NAME.KKB,
                        type: APPLICATION_BANK_STATUS_KKB.PROCESSING,
                      },
                      {
                        bank_name: BANK_NAME.KKB,
                        type: APPLICATION_BANK_STATUS_KKB.REVISION,
                      },
                    ]}
                  >
                    <Button
                      onClick={changeIndividual}
                      type="primary"
                      htmlType="submit"
                    >
                      Сохранить изменения
                    </Button>
                  </Guard.ApplicationBankStatus>
                </>
              )}
            </Form.Item>
          </div>
        ) : (
          <div className={classes.buttonWrapper}>
            <Form.Item>
              <Button
                style={{ color: "#3C79FF", borderColor: "#3C79FF" }}
                onClick={openOrCloseForm}
              >
                Отменить без сохранения
              </Button>
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                onClick={addNewIndividual}
                htmlType="submit"
              >
                Сохранить учредителя
              </Button>
            </Form.Item>
          </div>
        )}
      </Form>
    </div>
  );
};



